import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import { Button, message } from 'antd';
import classNames from 'classnames';
import aiLogo from '@/assets/icons/aiLogo.png';
import LoginModal from './login-modal/LoginModal';
import { toggleLoginModal } from '@/reducers/login-pop';
import logoText from '@/assets/img/logoText.png';
import sxnulogoText from '@/assets/img/sxnu-logo.png';
import pointsIcon from '@/assets/icons/points-1.svg';
import { ChevronDown } from 'lucide-react';
import useSXNU from '@/hooks/useSXNU';
import UserDropdown from './components/UserDropdown';
import NumberRoll from '@/components/number-roll/NumberRoll';

import { useTranslation } from 'react-i18next';
import useAuth from '@/hooks/useAuth';

const Navbar = (props) => {
  const { t, i18n } = useTranslation('common');
  const uid = useSelector((state) => state.users.uid);
  const userData = useSelector((state) => state.users.data);

  const { isSXNU } = useSXNU();
  const dispatch = useDispatch();
  const { hasVipPermission } = useAuth();

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        openDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleLogIn = () => {
    dispatch(toggleLoginModal(true));
  };

  const handleProtectedNavClick = (e, item) => {
    if (item.requiresAuth && !uid) {
      e.preventDefault();
      message.warning(
        t('login.requiredForFeature', '该功能需要登录，请先登录'),
      );
      dispatch(toggleLoginModal(true));
    }
  };

  const getHomePath = () => {
    return isSXNU ? '/sxnu' : '/';
  };

  const { visibleNavbarItem, visibleNavbarInfo } = useSelector(
    (state) => state.ui,
  );

  // 抽离
  const navItems = [
    { to: '/ailab', label: t('navbar.aiLab', 'AI体验') },
    { to: '/course', label: t('navbar.courses', '课程') },
    {
      to: '/modelTrainNew/projects',
      label: t('navbar.modelTrain', '模型训练'),
      requiresAuth: true,
    },
    { to: '/libs', label: t('navbar.aiTools', 'AI工具') },
    // { to: '/modelCommunity', label: '模型广场' },
    {
      to: '/teaching-management',
      label: t('navbar.teachingMgmt', '教学管理'),
      visible: hasVipPermission(0) || hasVipPermission(2),
    },
    {
      to: '/sx-subject',
      label: t('navbar.aiSubject', 'AI+学科'),
      visible: isSXNU,
    },
    {
      to: '/learning',
      label: t('navbar.myLearning', '我的学习'),
      visible: userData?.authInfo?.account_type === '学生',
    },
    {
      type: 'dropdown',
      key: 'coop',
      label: t('navbar.cooperation', '公益合作'),
      items: [
        { path: 'https://qf.zmrobo.com/', label: 'QuickForm', external: true },
        {
          path: '/playwithai',
          label: t('navbar.playWithAi', '学与玩'),
          external: false,
        },
      ],
    },
    { to: '/pricing', label: t('navbar.pricing', '订阅'), visible: !isSXNU },
  ];

  return (
    <header
      className={classNames(
        'sticky top-0 z-50 bg-[#2879f2] h-[var(--navber-height)]',
        props.className,
      )}
    >
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <NavLink to={getHomePath()} className="flex items-center gap-2 group">
            {isSXNU ? (
              <img
                src={sxnulogoText}
                alt="LOGO"
                style={{ height: '45px' }}
                draggable={false}
              />
            ) : (
              <>
                <img
                  src={aiLogo}
                  alt="LOGO"
                  className="w-10 h-10"
                  draggable={false}
                />
                <img
                  src={logoText}
                  alt="AI Logo Text"
                  className="w-[158px] h-5"
                />
              </>
            )}
          </NavLink>

          {/* Desktop Nav */}
          {visibleNavbarItem && (
            <nav className="flex items-center gap-1">
              {navItems.map((item) => {
                if (item.visible === false) return null;

                if (item.type === 'dropdown') {
                  const isOpen = openDropdown === item.key;
                  return (
                    <div
                      key={item.key}
                      className="relative"
                      ref={isOpen ? dropdownRef : null}
                    >
                      <button
                        onClick={() =>
                          setOpenDropdown(isOpen ? null : item.key)
                        }
                        className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm transition ${
                          isOpen
                            ? 'text-white bg-white/20'
                            : 'text-white hover:bg-white/15'
                        }`}
                      >
                        {item.label}
                        <ChevronDown
                          size={14}
                          className={`transition-transform ${
                            isOpen ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {isOpen && (
                        <div className="absolute left-0 mt-2 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                          {item.items.map((sub) =>
                            sub.external ? (
                              <a
                                key={sub.path}
                                href={sub.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setOpenDropdown(null)}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              >
                                {sub.label}
                              </a>
                            ) : (
                              <NavLink
                                key={sub.path}
                                to={sub.path}
                                state={{ fromNavbar: true }}
                                onClick={() => setOpenDropdown(null)}
                                className={({ isActive }) =>
                                  `w-full flex items-center px-4 py-2 text-sm ${
                                    isActive
                                      ? 'text-blue-600 bg-blue-50'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`
                                }
                              >
                                {sub.label}
                              </NavLink>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={(e) => handleProtectedNavClick(e, item)}
                    className={({ isActive }) =>
                      classNames(
                        'flex items-center px-[12px] py-[8px] rounded-full text-sm transition',
                        isActive
                          ? 'text-white bg-white/20'
                          : 'text-white hover:bg-white/15',
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          )}

          {/* Right Side */}
          {visibleNavbarInfo && (
            <div className="flex items-center gap-3">
              {/* 临时语言切换按钮，方便翻译核对 */}
              <Button
                onClick={() =>
                  i18n.changeLanguage(
                    i18n.language?.startsWith('zh') ? 'en' : 'zh',
                  )
                }
                className="!px-3 !py-1 !h-auto !rounded-full !bg-white/10 !text-white !text-xs !font-medium hover:!bg-white/20 !border-none"
              >
                {i18n.language?.startsWith('zh') ? 'EN' : '中'}
              </Button>

              {uid !== '' ? (
                <>
                  <NavLink
                    to="/personal-center/points"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-amber-300 transition hover:bg-white/15"
                  >
                    <img
                      src={pointsIcon}
                      alt=""
                      className="w-[15px] h-[15px]"
                    />
                    <NumberRoll
                      value={userData?.quotaInfo?.total_balance || 0}
                    />
                  </NavLink>
                  <UserDropdown userData={userData} isSXNU={isSXNU} />
                </>
              ) : (
                <Button
                  onClick={handleLogIn}
                  className="!px-5 !py-2 !h-auto !rounded-full !bg-white !text-[#2879f2] !text-sm !font-medium hover:!bg-white/90 !shadow-sm !border-none"
                >
                  {t('button.loginRegister', '登录/注册')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      <LoginModal onClose={() => dispatch(toggleLoginModal(false))} />
    </header>
  );
};

export default Navbar;
